import { motion } from 'framer-motion';

// viewBox 0..400 x, 0..200 y. Padding kept inside so nothing clips.
const X0 = 8;
const X1 = 392;
const samples = Array.from({ length: 41 }, (_, i) => {
  const t = i / 40;
  const x = X0 + (X1 - X0) * t;
  const capY = 150 - 120 * Math.pow(t, 0.5); // capability: steep then flattens high
  const ctrlY = 158 - 34 * t; // governance: slow, stays low
  return { x, capY, ctrlY };
});

const capPath = 'M' + samples.map((s) => `${s.x},${s.capY.toFixed(1)}`).join(' L');
const ctrlPath = 'M' + samples.map((s) => `${s.x},${s.ctrlY.toFixed(1)}`).join(' L');
const bandPath =
  capPath + ' L' + [...samples].reverse().map((s) => `${s.x},${s.ctrlY.toFixed(1)}`).join(' L') + ' Z';

const last = samples[samples.length - 1];
const mid = samples[Math.round(samples.length * 0.72)];

/**
 * "AI capability is outrunning governance." The shaded band between the two
 * lines is the story: the operating gap that keeps widening.
 */
export function GovernanceGapChart() {
  return (
    <div className="w-full">
      <svg viewBox="0 0 400 200" className="w-full" role="img" aria-label="AI capability outrunning governance">
        <defs>
          <linearGradient id="gapBand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(7,78,220,0.26)" />
            <stop offset="100%" stopColor="rgba(7,78,220,0.03)" />
          </linearGradient>
        </defs>

        {/* the gap band */}
        <motion.path
          d={bandPath}
          fill="url(#gapBand)"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.8 }}
        />

        {/* gap callout where it is widest */}
        <motion.g
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 1.4 }}
        >
          <line x1={mid.x} y1={mid.capY} x2={mid.x} y2={mid.ctrlY} stroke="rgba(255,140,0,0.6)" strokeWidth="1" strokeDasharray="3 3" />
          <text x={mid.x - 8} y={(mid.capY + mid.ctrlY) / 2} textAnchor="end" fill="#FF8C00" style={{ fontSize: 11 }}>
            the gap
          </text>
        </motion.g>

        {/* governance line */}
        <motion.path
          d={ctrlPath}
          fill="none"
          stroke="#64748b"
          strokeWidth="2.5"
          strokeDasharray="5 5"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: 'easeInOut' }}
        />
        {/* capability line */}
        <motion.path
          d={capPath}
          fill="none"
          stroke="#074EDC"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: 'easeInOut' }}
        />

        {/* endpoint markers + labels */}
        <circle cx={last.x} cy={last.capY} r="4" fill="#074EDC" />
        <text x={last.x - 6} y={last.capY - 8} textAnchor="end" fill="#074EDC" style={{ fontSize: 11 }}>
          AI capability
        </text>
        <circle cx={last.x} cy={last.ctrlY} r="4" fill="#94a3b8" />
        <text x={last.x - 6} y={last.ctrlY + 16} textAnchor="end" fill="#94a3b8" style={{ fontSize: 11 }}>
          Governance
        </text>
      </svg>

      <div className="mt-2 flex justify-between text-gray-400">
        <span>Today</span>
        <span>AI adoption over time</span>
        <span>Near future</span>
      </div>
    </div>
  );
}
